import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { cn } from '@shared/utils/utils';
import Tags from '@shared/components/ui/Tags/Tags';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
interface AddPaymentMethodButtonProps {
  linkedPaymentMethods?: Array<{ id: number; title: string }>;
  paymentMethodId?: number; // ID de la categoría (1=paid, 2=bank, 3=cash)
  onAddMethod?: (title: string, paymentMethodId: number) => void;
  onRemoveMethod?: (methodId: number) => void;
}

export const AddPaymentMethodButton = ({
  linkedPaymentMethods = [],
  paymentMethodId = 1,
  onAddMethod,
  onRemoveMethod,
}: AddPaymentMethodButtonProps) => {
  const { t } = useTranslation('shopsModule');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<string[]>(linkedPaymentMethods.map((method) => method.title));
  const [inputValue, setInputValue] = useState('');
  const confirmModalRef = useRef<ConfirmModalRef>(null);

  // Sincronizar paymentMethods cuando cambien linkedPaymentMethods
  useEffect(() => {
    setPaymentMethods(linkedPaymentMethods.map((method) => method.title));
  }, [linkedPaymentMethods]);

  const handleClick = () => {
    setIsExpanded(true);
  };

  const handleInputBlur = () => {
    setIsExpanded(false);
    setInputValue('');
  };

  const handleRemovePaymentMethod = (methodToRemove: string) => {
    confirmModalRef.current?.open(
      () => {
        // Encontrar el método por título y eliminarlo
        const methodToRemoveObj = linkedPaymentMethods.find((method) => method.title === methodToRemove);
        if (methodToRemoveObj && onRemoveMethod) {
          onRemoveMethod(methodToRemoveObj.id);
        }
      },
      undefined,
      t('modals.settings.payments.deletePaymentMethod.title'),
      t('modals.settings.payments.deletePaymentMethod.message', {
        method: methodToRemove,
      }),
    );
  };

  return (
    <>
      {/* Renderizar los tags de métodos de pago */}
      {paymentMethods.map((method, index) => (
        <Tags key={index} onRemove={() => handleRemovePaymentMethod(method)} dataTestId={`payment-method-tag-${index}`}>
          {method}
        </Tags>
      ))}

      {/* Input para agregar nuevo método (solo visible cuando está expandido) */}
      {isExpanded && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (inputValue.trim() && onAddMethod) {
              onAddMethod(inputValue.trim(), paymentMethodId);
              setInputValue('');
              setIsExpanded(false);
            }
          }}
          className="inline"
        >
          <input
            type="text"
            placeholder={t('modals.settings.payments.addPaymentMethod.placeholder')}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                if (inputValue.trim() && onAddMethod) {
                  onAddMethod(inputValue.trim(), paymentMethodId);
                  setInputValue('');
                  setIsExpanded(false);
                }
              }
            }}
            onBlur={handleInputBlur}
            className="bg-transparent border border-card-border rounded-full text-xs h-7 px-3 placeholder:text-complementary-text"
            data-testid="payment-method-input"
            autoFocus
          />
        </form>
      )}

      {/* Botón para agregar nuevo método (oculto cuando el input está activo) */}
      {!isExpanded && (
        <div
          className="relative h-7 w-7 shrink-0 overflow-visible"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Píldora: padding horizontal reducido (px-2); mismo flex start en hover para que el + no salte - Tighter padding; stable icon position */}
          <Button
            variant="outline"
            size="icon"
            className={cn(
              'absolute left-0 top-0 z-10 flex h-7 items-center overflow-hidden whitespace-nowrap rounded-full border-0 bg-card-border font-bold !text-icons transition-[width,min-width,max-width,padding,gap] duration-200 ease-out dark:!bg-[#2C3442]',
              isHovered ? 'min-w-7 w-max gap-1.5 px-2' : 'w-7 min-w-[1.75rem] max-w-[1.75rem] gap-0 px-2',
            )}
            data-testid="add-payment-method-button"
            onClick={handleClick}
          >
            <FontAwesomeIcon icon={faPlus} className="!h-3 !w-3 shrink-0 transition-none" />
            <span
              className={cn(
                'min-w-0 overflow-hidden font-normal text-xs',
                isHovered
                  ? 'max-w-[12rem] opacity-100 transition-[max-width,opacity] duration-200 ease-out'
                  : 'max-w-0 opacity-0 transition-none',
              )}
              aria-hidden={!isHovered}
            >
              {t('modals.settings.payments.addPaymentMethod.button')}
            </span>
          </Button>
        </div>
      )}

      {/* Modal de confirmación para eliminar métodos de pago */}
      <ConfirmModal ref={confirmModalRef} />
    </>
  );
};
